import { Link } from 'react-router-dom';
import { Linkedin, Instagram, Mail } from 'lucide-react';
import SEO from '@/components/SEO';

const AboutPage = () => {
  return (
    <div data-testid="about-page" className="min-h-screen bg-[#FAF9F6]">
      <SEO title="About She Built This" description="A research platform documenting 595+ verified women who built empires. Founded by Dr. Neha Dewan." url="https://shebuiltthis.com/about" />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-[900px] mx-auto text-center">
          <p className="text-[10px] tracking-[0.35em] uppercase mb-6" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.2)' }}>ABOUT</p>
          <h1 className="text-5xl lg:text-[72px] leading-[0.95] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: '#F4F0E6' }}>She Built This</h1>
          <p className="text-xl italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#C25E3E' }}>Real stories of women who built something real.</p>
        </div>
      </section>

      {/* The Platform */}
      <section className="bg-[#FAF9F6] py-24 lg:py-32 border-t border-[rgba(201,168,76,0.08)]">
        <div className="max-w-[900px] mx-auto px-6">
          <p className="text-[10px] tracking-[0.3em] uppercase mb-6" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>THE PLATFORM</p>
          <h2 className="text-4xl mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#F4F0E6' }}>What we built</h2>
          <p className="text-lg leading-relaxed mb-6" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.45)' }}>
            She Built This is a research platform documenting women who have built something significant — companies, institutions, policies, movements. Founded by Dr. Neha Dewan in 2025-2026, the platform now includes 595+ verified profiles across 14 regions and every continent.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-[1px] bg-[rgba(201,168,76,0.08)] my-10">
            {[{ v: '595+', l: 'Verified Profiles' }, { v: '14', l: 'Regions' }, { v: '7', l: 'Continents' }, { v: '30+', l: 'Industries' }].map((s) => (
              <div key={s.l} className="bg-[#FAF9F6] text-center p-6">
                <p className="text-4xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#C25E3E' }}>{s.v}</p>
                <p className="text-[9px] tracking-[0.2em] uppercase" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.3)' }}>{s.l}</p>
              </div>
            ))}
          </div>
          <p className="text-lg leading-relaxed" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.45)' }}>
            Every fact on this platform is verified against official sources. Every photograph is authenticated. We do not publish AI-generated images. We do not publish unverified claims. This is not a magazine feature — it is evidence.
          </p>
        </div>
      </section>

      {/* The Research */}
      <section className="py-24 lg:py-32 border-t border-[rgba(201,168,76,0.08)]">
        <div className="max-w-[900px] mx-auto px-6">
          <p className="text-[10px] tracking-[0.3em] uppercase mb-6" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.2)' }}>THE RESEARCH</p>
          <h2 className="text-4xl mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#F4F0E6' }}>What the evidence shows</h2>
          <p className="text-lg leading-relaxed mb-8" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.45)' }}>
            The research behind She Built This is a nine-framework systematic analysis of fifty years of evidence on women in leadership. Dr. Neha Dewan's doctoral research examined what actually works to advance women to senior positions — and what doesn't.
          </p>
          <div className="border-l-2 border-[#C25E3E] pl-8 py-4 my-10">
            <p className="text-2xl lg:text-3xl italic leading-[1.4]" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#F4F0E6' }}>
              After fifty years of studies, billions spent on programs, the number of interventions proven to increase women in leadership over time is: <span style={{ color: '#C25E3E' }}>Zero.</span>
            </p>
          </div>
          <p className="text-lg leading-relaxed" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.45)' }}>
            The research is currently under review at SAGE Open journal. She Built This exists to document what the data cannot fully capture: the women who built anyway, inside systems never designed to help them succeed.
          </p>
        </div>
      </section>

      {/* The Film */}
      <section className="bg-[#FAF9F6] py-24 lg:py-32 border-t border-[rgba(201,168,76,0.08)]">
        <div className="max-w-[900px] mx-auto px-6">
          <p className="text-[10px] tracking-[0.3em] uppercase mb-6" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>THE FILM</p>
          <h2 className="text-4xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#F4F0E6' }}>The Labyrinth</h2>
          <p className="text-lg leading-relaxed mb-6" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.45)' }}>
            A documentary film about women in leadership. Not inspirational. Not corporate. The labyrinth metaphor describes the experience more accurately than any glass ceiling — and it demanded a visual treatment.
          </p>
          <Link to="/film" className="inline-flex items-center text-[11px] tracking-[0.12em] uppercase text-[#C25E3E] hover:text-[#1A1A1A] transition-colors" style={{ fontFamily: "'DM Mono', monospace" }}>Watch the Sizzle Reel →</Link>
        </div>
      </section>

      {/* The Founder */}
      <section className="py-24 lg:py-32 border-t border-[rgba(201,168,76,0.08)]">
        <div className="max-w-[900px] mx-auto px-6">
          <p className="text-[10px] tracking-[0.3em] uppercase mb-6" style={{ fontFamily: "'DM Mono', monospace", color: 'rgba(250,248,244,0.2)' }}>THE FOUNDER</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div>
              <div className="aspect-square bg-[#C25E3E] flex items-center justify-center">
                <span className="text-7xl font-bold text-[#080808]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>ND</span>
              </div>
            </div>
            <div className="lg:col-span-2">
              <h2 className="text-4xl mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, color: '#F4F0E6' }}>Dr. Neha Dewan</h2>
              <p className="text-lg leading-relaxed mb-6" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.45)' }}>
                Founder of She Built This. Co-Founder & CEO of SKI APAC FZCO, Dubai Silicon Oasis. Her research — a nine-framework systematic analysis of fifty years of evidence — is currently under review at SAGE Open journal.
              </p>
              <p className="text-lg leading-relaxed mb-8" style={{ fontFamily: "'Lora', serif", color: 'rgba(250,248,244,0.45)' }}>
                She Built This began as a documentation project. It evolved into a research platform. It is now becoming a movement.
              </p>
              <div className="flex items-center gap-4 mb-8">
                <a href="https://skiasiapacific.com" target="_blank" rel="noopener noreferrer" className="text-[11px] tracking-[0.1em] uppercase hover:text-[#1A1A1A] transition-colors" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>skiasiapacific.com</a>
                <span className="text-[rgba(250,248,244,0.15)]">|</span>
                <a href="https://sentinel-uae.com" target="_blank" rel="noopener noreferrer" className="text-[11px] tracking-[0.1em] uppercase hover:text-[#1A1A1A] transition-colors" style={{ fontFamily: "'DM Mono', monospace", color: '#C25E3E' }}>sentinel-uae.com</a>
              </div>
              <Link to="/profiles/dr-neha-dewan" className="inline-flex items-center h-12 px-8 border border-[#C25E3E] text-[#C25E3E] text-[12px] tracking-[0.1em] uppercase transition-all hover:bg-[#C25E3E] hover:text-[#080808]" style={{ fontFamily: "'DM Mono', monospace" }}>View Profile →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-[#C25E3E] py-20 lg:py-24">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-[52px] leading-[1.05] mb-10" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: '#080808' }}>Contact</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { href: 'mailto:support@shebuiltthis.com', icon: Mail, label: 'support@shebuiltthis.com' },
              { href: 'https://www.instagram.com/she_builtthis/', icon: Instagram, label: '@she_builtthis' },
              { href: 'https://www.linkedin.com/company/shebuiltthis/', icon: Linkedin, label: 'She Built This' },
            ].map(({ href, icon: Icon, label }) => (
              <a key={label} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="flex flex-col items-center gap-3 p-6 bg-[#FAF9F6]/10 hover:bg-[#FAF9F6]/20 transition-colors">
                <Icon size={24} className="text-[#080808]" />
                <span className="text-[11px] tracking-[0.08em]" style={{ fontFamily: "'DM Mono', monospace", color: '#080808' }}>{label}</span>
              </a>
            ))}
          </div>
          <p className="mt-8" style={{ fontFamily: "'Lora', serif", color: 'rgba(10,10,10,0.6)' }}>Dubai, UAE</p>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
